import { useEffect, useState, useCallback } from "react";
import { Plus, Trash2, Play, Plug, Code2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { api, errMsg } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useAuth, EDITOR_ROLES } from "../context/AuthContext";
import { PageHeader, Card } from "../components/Card";
import { FilterSelect } from "../components/DataTable";
import { StatusPill } from "../components/Severity";

const INT_TYPES = ["llm", "speech_to_text", "text_to_speech", "email", "webhook", "itsm", "siem", "storage", "other"];
const ACTIONS = ["entity_stats", "llm_prompt", "webhook"];

export default function Integrations() {
  const { t } = useI18n();
  const { isAdmin, hasRole } = useAuth();
  const [ints, setInts] = useState([]);
  const [fns, setFns] = useState([]);
  const [newInt, setNewInt] = useState(null);
  const [newFn, setNewFn] = useState(null);
  const [outputs, setOutputs] = useState({});
  const load = useCallback(() => {
    api.get("/integrations").then((r) => setInts(r.data));
    api.get("/functions").then((r) => setFns(r.data));
  }, []);
  useEffect(() => { load(); }, [load]);
  const act = async (fn) => { try { await fn(); toast.success(t("saved")); load(); } catch (e) { toast.error(errMsg(e)); } };

  const saveFn = () => {
    let config;
    try { config = JSON.parse(newFn.config || "{}"); } catch (_) { return toast.error("Invalid JSON"); }
    act(async () => { await api.post("/functions", { ...newFn, config }); setNewFn(null); });
  };
  const run = async (f) => {
    setOutputs((o) => ({ ...o, [f.id]: "…" }));
    try { const { data } = await api.post(`/functions/${f.id}/run`); setOutputs((o) => ({ ...o, [f.id]: `${data.output} (${data.ms}ms)` })); }
    catch (e) { setOutputs((o) => ({ ...o, [f.id]: errMsg(e) })); }
  };

  return (
    <div>
      <PageHeader title={t("nav_integrations")} sub={`${ints.length} ${t("integrations")} · ${fns.length} ${t("functions")}`} />
      <Tabs defaultValue="int">
        <TabsList className="mb-4 rounded-xl"><TabsTrigger value="int" data-testid="tab-integrations">{t("integrations")}</TabsTrigger><TabsTrigger value="fn" data-testid="tab-functions">{t("functions")}</TabsTrigger></TabsList>
        <TabsContent value="int">
          {isAdmin && <Button data-testid="add-integration-button" onClick={() => setNewInt({ name: "", type: "webhook", endpoint: "", status: "disabled" })} className="mb-4 rounded-xl bg-slate-900"><Plus className="h-4 w-4" />{t("addIntegration")}</Button>}
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="integrations-grid">
            {ints.map((i) => (
              <Card key={i.id} data-testid={`integration-card-${i.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-3"><div className="flex h-10 w-10 items-center justify-center rounded-xl bg-sky-50"><Plug className="h-5 w-5 text-sky-700" /></div>
                    <div><p className="font-semibold text-slate-900">{i.name}</p><p className="font-mono text-xs text-slate-500">{i.type}</p></div></div>
                  {isAdmin && <Button size="icon" variant="ghost" data-testid={`delete-integration-${i.id}`} onClick={() => act(() => api.delete(`/integrations/${i.id}`))}><Trash2 className="h-4 w-4 text-rose-700" /></Button>}
                </div>
                <p className="mt-3 truncate font-mono text-xs text-slate-500" dir="ltr">{i.endpoint}</p>
                <div className="mt-3 flex items-center justify-between">
                  <StatusPill value={i.status} />
                  {isAdmin && <button data-testid={`toggle-integration-${i.id}`} onClick={() => act(() => api.patch(`/integrations/${i.id}`, { name: i.name, type: i.type, endpoint: i.endpoint, status: i.status === "active" ? "disabled" : "active" }))} className="text-xs font-medium text-sky-700 hover:underline">{i.status === "active" ? t("disabled") : t("active")}</button>}
                </div>
              </Card>
            ))}
          </div>
        </TabsContent>
        <TabsContent value="fn">
          {isAdmin && <Button data-testid="add-function-button" onClick={() => setNewFn({ name: "", action: "entity_stats", config: '{"entity": "risks"}', description: "" })} className="mb-4 rounded-xl bg-slate-900"><Plus className="h-4 w-4" />{t("addFunction")}</Button>}
          <div className="grid gap-4 lg:grid-cols-2" data-testid="functions-grid">
            {fns.map((f) => (
              <Card key={f.id} data-testid={`function-card-${f.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-3"><Code2 className="h-5 w-5 text-slate-700" /><div><p className="font-semibold text-slate-900">{f.name}</p><p className="text-xs text-slate-500">{f.description}</p></div></div>
                  <div className="flex gap-1">
                    {hasRole(EDITOR_ROLES) && <Button size="sm" data-testid={`run-function-${f.id}`} onClick={() => run(f)} className="rounded-lg bg-sky-600 hover:bg-sky-700"><Play className="h-3.5 w-3.5" />{t("run")}</Button>}
                    {isAdmin && <Button size="icon" variant="ghost" data-testid={`delete-function-${f.id}`} onClick={() => act(() => api.delete(`/functions/${f.id}`))}><Trash2 className="h-4 w-4 text-rose-700" /></Button>}
                  </div>
                </div>
                <p className="mt-3 font-mono text-xs text-slate-500" dir="ltr">{f.action} · {JSON.stringify(f.config)}</p>
                {outputs[f.id] && <p data-testid={`function-output-${f.id}`} className="mt-3 rounded-xl bg-slate-50 p-3 text-sm text-slate-700" dir="auto">{outputs[f.id]}</p>}
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>
      <Dialog open={!!newInt} onOpenChange={(o) => !o && setNewInt(null)}>
        <DialogContent data-testid="integration-dialog">
          <DialogHeader><DialogTitle>{t("addIntegration")}</DialogTitle></DialogHeader>
          {newInt && <div className="space-y-3">
            <Input data-testid="integration-name-input" placeholder={t("name")} value={newInt.name} onChange={(e) => setNewInt({ ...newInt, name: e.target.value })} className="rounded-xl" />
            <FilterSelect value={newInt.type} onChange={(v) => setNewInt({ ...newInt, type: v })} options={INT_TYPES} placeholder={t("type")} testId="integration-type-select" allowAll={false} />
            <Input data-testid="integration-endpoint-input" dir="ltr" placeholder="https://" value={newInt.endpoint} onChange={(e) => setNewInt({ ...newInt, endpoint: e.target.value })} className="rounded-xl" />
          </div>}
          <DialogFooter><Button data-testid="integration-save-button" onClick={() => act(async () => { await api.post("/integrations", newInt); setNewInt(null); })} className="rounded-xl bg-slate-900">{t("save")}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!newFn} onOpenChange={(o) => !o && setNewFn(null)}>
        <DialogContent data-testid="function-dialog">
          <DialogHeader><DialogTitle>{t("addFunction")}</DialogTitle></DialogHeader>
          {newFn && <div className="space-y-3">
            <Input data-testid="function-name-input" placeholder={t("name")} value={newFn.name} onChange={(e) => setNewFn({ ...newFn, name: e.target.value })} className="rounded-xl" />
            <FilterSelect value={newFn.action} onChange={(v) => setNewFn({ ...newFn, action: v })} options={ACTIONS} placeholder={t("action")} testId="function-action-select" allowAll={false} />
            <Input data-testid="function-description-input" placeholder={t("description")} value={newFn.description} onChange={(e) => setNewFn({ ...newFn, description: e.target.value })} className="rounded-xl" />
            <Textarea data-testid="function-config-input" dir="ltr" value={newFn.config} onChange={(e) => setNewFn({ ...newFn, config: e.target.value })} className="rounded-xl font-mono text-xs" aria-label={t("config")} />
          </div>}
          <DialogFooter><Button data-testid="function-save-button" onClick={saveFn} className="rounded-xl bg-slate-900">{t("save")}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
